import { useCallback, useRef } from 'react'

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

let loadPromise = null

/** Injects Razorpay's checkout.js once and reuses the same promise on re-entry. */
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true)
  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = CHECKOUT_SRC
    script.async = true
    script.onload = () => resolve(true)
    script.onerror = () => {
      loadPromise = null
      resolve(false)
    }
    document.body.appendChild(script)
  })
  return loadPromise
}

/**
 * Opens the Razorpay checkout modal for an already-created order.
 * Resolves with the payment response on success, rejects on failure/dismiss.
 *
 * Key resolution order: an explicit `keyId` (e.g. resolved live from
 * GET /payment-gateways) wins, then `order.key` if the order-create response
 * echoed one back, then VITE_RAZORPAY_KEY as a last-resort local override.
 * If none are available, rejects with a clearly-tagged
 * `{ code: 'no_gateway_key' }` error so callers can fall back gracefully
 * instead of throwing an opaque Razorpay error.
 */
export function useRazorpayCheckout() {
  const openingRef = useRef(false)

  const open = useCallback(async ({ order, name, description, prefill, keyId: keyIdOverride, themeColor = '#5a0a16' }) => {
    if (openingRef.current) return Promise.reject(new Error('Checkout already open'))

    const keyId = keyIdOverride ?? order?.key ?? import.meta.env.VITE_RAZORPAY_KEY
    if (!keyId) {
      const err = new Error('Payment gateway is not configured yet.')
      err.code = 'no_gateway_key'
      throw err
    }

    const ready = await loadRazorpayScript()
    if (!ready || !window.Razorpay) {
      const err = new Error('Could not load the payment gateway. Check your connection and try again.')
      err.code = 'script_load_failed'
      throw err
    }

    openingRef.current = true

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: name || 'Astrologer Raju Maharaj Ji',
        description: description || 'Consultation booking',
        order_id: order.razorpay_order_id || order.order_id || order.id,
        prefill: prefill || {},
        theme: { color: themeColor },
        handler: (response) => {
          openingRef.current = false
          resolve(response)
        },
        modal: {
          ondismiss: () => {
            openingRef.current = false
            reject(Object.assign(new Error('Payment was cancelled.'), { code: 'cancelled' }))
          },
        },
      })
      rzp.on('payment.failed', (response) => {
        openingRef.current = false
        reject(Object.assign(new Error(response?.error?.description || 'Payment failed.'), { code: 'payment_failed', response }))
      })
      rzp.open()
    })
  }, [])

  return { open }
}
